// client/src/components/history/EventHistory.jsx
//
// Tenant-facing entry point for the article-style public event history.
// Shows the tenant's events exactly like the Events component (EventList);
// tapping an event opens its article editor (EventHistoryArticle).

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import AppFooter from "../AppFooter";
import { FaArrowLeft, FaHome, FaTh, FaList } from "react-icons/fa";
import SearchFilterBar from "../shared/SearchFilterBar";
import { toAbs, isVideo, isImage, pickMediaFromEvent } from "../../utils/eventMedia";

const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:5000";

const parseDate = (value) => {
  if (!value) return null;
  const d = new Date(String(value).includes(" ") ? String(value).replace(" ", "T") : value);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Same display rule as EventList: hide the tenant default "Timeline" event and
// inactive events; keep everything else (past events included — that's the point
// of a history view).
const isTimelineEvent = (evt) => {
  const name = String(evt?.name ?? "").trim().toLowerCase();
  const type = String(evt?.type ?? evt?.eventtype ?? "").trim().toLowerCase();
  return name === "timeline" || type === "timeline";
};
const shouldDisplay = (evt) => {
  if (isTimelineEvent(evt)) return false;
  const hasActive = evt?.active !== undefined && evt?.active !== null;
  return !hasActive || evt.active === true || evt.active === 1 || evt.active === "1";
};

export default function EventHistory() {
  const { tenant } = useParams();
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("os_eventhistory_view") || "single"; } catch { return "single"; }
  });
  const setViewModeAndPersist = (mode) => {
    setViewMode(mode);
    try { localStorage.setItem("os_eventhistory_view", mode); } catch {}
  };

  useEffect(() => {
    document.body.classList.add("eventlist-fullbleed");
    return () => document.body.classList.remove("eventlist-fullbleed");
  }, []);

  useEffect(() => {
    if (!tenant) { setErr("Tenant is missing."); setLoading(false); return; }
    let alive = true;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const res = await axios.get(`${baseUrl}/api/events/${tenant}/events`);
        if (!alive) return;
        if (res?.data?.success) {
          setEvents(Array.isArray(res.data.data) ? res.data.data : []);
        } else {
          throw new Error(res?.data?.message || "Failed to load events");
        }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.message || "Failed to load events");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [tenant]);

  const visibleEvents = useMemo(() => {
    let result = (events || []).filter(shouldDisplay);
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      result = result.filter((evt) => {
        const name = (evt?.name || "").toLowerCase();
        const desc = (evt?.description || "").toLowerCase();
        return name.includes(q) || desc.includes(q);
      });
    }
    if (dateFrom) {
      const from = new Date(dateFrom);
      result = result.filter((evt) => {
        const s = parseDate(evt?.startdate);
        return s ? s >= from : true;
      });
    }
    if (dateTo) {
      const to = new Date(dateTo + "T23:59:59");
      result = result.filter((evt) => {
        const s = parseDate(evt?.startdate);
        return s ? s <= to : true;
      });
    }
    result.sort((a, b) => (parseDate(b?.startdate) || new Date(0)) - (parseDate(a?.startdate) || new Date(0)));
    return result;
  }, [events, searchTerm, dateFrom, dateTo]);

  const openArticle = (eventId) => navigate(`/${tenant}/${eventId}/eventHistoryArticle`);

  return (
    <div className={`feed-page feed-page--${viewMode}`}>
      <div className={`feed feed--${viewMode}`}>
        <div className="flex items-center justify-between mb-2 px-2" style={{ gridColumn: "1 / -1" }}>
          <h3 className="m-0">Event History</h3>
        </div>

        <SearchFilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onClear={() => { setSearchTerm(""); setDateFrom(""); setDateTo(""); }}
          searchPlaceholder={"Search events…"}
        />

        <div className="eventlist-view-toggle" style={{ gridColumn: "1 / -1" }}>
          <button
            type="button"
            className={`view-toggle-btn ${viewMode === "single" ? "active" : ""}`}
            onClick={() => setViewModeAndPersist("single")}
            aria-label="Single column"
          >
            <FaList />
          </button>
          <button
            type="button"
            className={`view-toggle-btn ${viewMode === "grid" ? "active" : ""}`}
            onClick={() => setViewModeAndPersist("grid")}
            aria-label="Grid 3 across"
          >
            <FaTh />
          </button>
        </div>

        {err && (
          <div className="alert alert-danger" style={{ gridColumn: "1 / -1" }}>{err}</div>
        )}

        {loading && (
          <div style={{ textAlign: "center", padding: "24px 0", color: "#a8a8a8", gridColumn: "1 / -1" }}>
            Loading…
          </div>
        )}

        {!loading && !err && visibleEvents.length === 0 && (
          <div style={{ textAlign: "center", padding: "24px 0", color: "#a8a8a8", gridColumn: "1 / -1" }}>
            {searchTerm || dateFrom || dateTo ? "No events match your filters." : "No events yet."}
          </div>
        )}

        {visibleEvents.map((evt) => {
          const media = pickMediaFromEvent(evt);
          const src = toAbs(baseUrl, media?.path || media?.url || media);
          const vid = isVideo(media);
          const img = isImage(media);
          const go = () => openArticle(evt.id);

          return (
            <article key={evt.id} className="feed-card feed-card--uniform">
              <div
                className="feed-card__header feed-card__header--single"
                onClick={go}
                style={{ cursor: "pointer" }}
              >
                <div className="feed-card__header-text">
                  <div className="feed-card__title feed-card__title--single">
                    {evt?.name || `Event ${evt?.id}`}
                  </div>
                </div>
              </div>

              <div className="feed-card__media-wrap" onClick={go} style={{ cursor: "pointer" }}>
                {vid ? (
                  <video
                    className="feed-media feed-media--uniform"
                    src={src}
                    muted
                    playsInline
                    preload="metadata"
                  />
                ) : (
                  <img
                    className="feed-media feed-media--uniform"
                    src={img ? src : toAbs(baseUrl, "/public/event.png")}
                    alt={evt?.name || "Event"}
                  />
                )}
              </div>

              {evt?.description && (
                <div className="feed-card__body feed-card__body--single">
                  <div className="feed-card__desc--single" title={evt.description}>
                    {evt.description}
                  </div>
                </div>
              )}
            </article>
          );
        })}

        <AppFooter
          buttons={[
            { icon: <FaArrowLeft />, label: "Back", onClick: () => navigate(-1) },
            { icon: <FaHome />, label: "Home", onClick: () => navigate(`/${tenant}/dashboard`) },
          ]}
        />
      </div>
    </div>
  );
}
